import { Component, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { VideoconferenceImpl } from './videoconference.impl';
import { IModalComponent, IModalComponentRef, ModalOverlayService, ModalWindowRef } from '@lib/modal';
import { EProfileTypeDTO, IRequisition, IRequisitionParticipant } from '@project/view-models';
import { VideoCallManagerService } from 'src/app/features/modules/video-call/video-call-manager.service';
import { TranslateService } from '@project/translate';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ModalQrCodeComponent } from '../../project/components/modal-qr-code/modal-qr-code.component';
import { QrCodeService } from '../../project/components/modal-qr-code/qr-code.service';
import { NotificationsService } from '@lib/notifications';
import { AvailableEmbeddedAppFeatures, EmbeddedTcAppService, HcService, UploadSessionService } from '@project/services';
import { monitorLogger } from '../../project/monitor/monitor-logger';
import { debounceTime } from 'rxjs/operators';
import { HCConfigFeatures } from 'src/hc-config/hc-config';
import { MediaDevicePreferencesService } from './services/media-device-preferences.service';

const QR_SESSION_FILES_DEBOUNCE_MS = 1500;

@Component({
  selector: 'app-videoconference',
  templateUrl: './videoconference.component.html',
  styleUrls: ['./videoconference.component.scss'],
})
export class VideoconferenceComponent implements OnInit, IModalComponent, OnDestroy {
  private qrCodeBehaviorSubject$ = new BehaviorSubject<string>(null);
  public qrCodeObservable = this.qrCodeBehaviorSubject$.asObservable();
  private timerBehaviorSubject$ = new BehaviorSubject<{ minutes: number; seconds: number }>({
    minutes: 5,
    seconds: 0,
  });
  public timerObservable = this.timerBehaviorSubject$.asObservable();
  private isQrCodeExpiredBehaviorSubject$ = new BehaviorSubject<boolean>(true);
  public isQrCodeExpiredObservable = this.isQrCodeExpiredBehaviorSubject$.asObservable();
  private hasQrCodeBeenGenerated = false;
  private isQrCodeActive = false;
  private countdownInterval: ReturnType<typeof setInterval>;
  private uploadSessionFilesSubscription: Subscription | null = null;
  private isSubmittingActiveQrSessionFiles = false;
  public readonly mainProfileTypeDTO: IRequisitionParticipant = this.videoCallManagerService.participant;
  public readonly secundaryProfileTypeDTO: IRequisitionParticipant = this.videoCallManagerService
    .myProfile as IRequisitionParticipant;
  public readonly requisition: IRequisition = this.videoCallManagerService.requisition;

  public showChat = true;
  public isPatient = false;
  public isPreviewMode = true;
  public canManageMediaDevices = [EProfileTypeDTO.Doctor, EProfileTypeDTO.Patient].includes(
    this.secundaryProfileTypeDTO.profileType,
  );
  public canSkipPreview = this.secundaryProfileTypeDTO.profileType === EProfileTypeDTO.Doctor;
  public skipPreviewNextTime = false;
  public showDeviceSettings = false;
  public canOpenDeviceSettings = false;

  public readonly close$ = new EventEmitter();
  public readonly submit$ = new EventEmitter();

  private disableChatBehavior$ = new BehaviorSubject<boolean>(false);
  public disableChatObservable$ = this.disableChatBehavior$.asObservable();

  private disableRenderChat =
    this.secundaryProfileTypeDTO.profileType === EProfileTypeDTO.Doctor ||
    this.secundaryProfileTypeDTO.profileType === EProfileTypeDTO.Receptionist;

  private ModalQrCodeRef: IModalComponentRef<ModalQrCodeComponent>;

  constructor(
    public videoconference: VideoconferenceImpl,
    private modalOverlayService: ModalOverlayService,
    private videoCallManagerService: VideoCallManagerService,
    public modalWindowRef: ModalWindowRef,
    private qrCodeService: QrCodeService,
    private notificationService: NotificationsService,
    private hcService: HcService,
    private embeddedTcAppService: EmbeddedTcAppService,
    private uploadSessionService: UploadSessionService,
    private mediaDevicePreferencesService: MediaDevicePreferencesService,
  ) {}

  ngOnInit(): void {
    this.isPreviewMode = this.shouldShowPreview();
    this.canOpenDeviceSettings = this.canManageMediaDevices && this.isCameraMicPreviewEnabled();

    if (!this.isPreviewMode) {
      this.join();
    }

    this.observerEvents();
    this.setupModal();
    this.renderChat();
    this.checkIfPatientType();
  }

  private isCameraMicPreviewEnabled(): boolean {
    return this.hcService.hasFeature(HCConfigFeatures.CAMERA_MIC_PREVIEW_BEFORE_CALL_JOIN);
  }

  private shouldShowPreview(): boolean {
    if (!this.isCameraMicPreviewEnabled()) {
      return false;
    }

    return !(this.canSkipPreview && this.mediaDevicePreferencesService.shouldSkipPreviewBeforeCall());
  }

  public toggleDeviceSettings(): void {
    if (!this.showDeviceSettings) {
      monitorLogger.screen.trackScreen('videoconference', 'abrir_configuracoes_dispositivos');
    }

    this.showDeviceSettings = !this.showDeviceSettings;
  }

  startCountdown() {
    if (this.countdownInterval) {
      return;
    }

    this.countdownInterval = setInterval(() => {
      let { minutes, seconds } = this.timerBehaviorSubject$.value;

      if (seconds === 0) {
        if (minutes === 0) {
          this.isQrCodeExpiredBehaviorSubject$.next(true);
          this.clearCountdown();
          this.isQrCodeActive = false;
          this.uploadSessionService.expireSession();
        } else {
          minutes--;
          seconds = 59;
        }
      } else {
        seconds--;
      }

      this.timerBehaviorSubject$.next({ minutes, seconds });
    }, 1000);
  }

  clearCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  public checkIfPatientType(): void {
    this.isPatient = this.secundaryProfileTypeDTO.profileType === EProfileTypeDTO.Patient;
  }

  public async generateQrCode(shouldReactivateSession = false) {
    this.isQrCodeExpiredBehaviorSubject$.next(false);
    this.qrCodeBehaviorSubject$.next(null);
    this.clearCountdown();

    this.timerBehaviorSubject$.next({ minutes: 5, seconds: 0 });

    try {
      const sessionId = await (shouldReactivateSession
        ? this.uploadSessionService.regenerateSession()
        : this.uploadSessionService.startSession()
      ).toPromise();
      const response = await this.qrCodeService.getAuthCode().toPromise();
      const healthCenterParam = this.hcService.getHcConfig().config.hcParam;
      const qrCodeUrl = this.embeddedTcAppService.getPublicEmbeddedFeatureUrl(
        AvailableEmbeddedAppFeatures.ExternalUpload,
        {
          session: sessionId,
          chatId: this.requisition.chatId,
          external_auth_code: response.external_auth_code,
          requisitionId: this.requisition.id,
          hc: healthCenterParam,
        },
        { shouldSanitize: false },
      );
      this.subscribeToActiveQrSessionFiles();
      this.qrCodeBehaviorSubject$.next(qrCodeUrl);
      this.startCountdown();
    } catch (error) {
      this.isQrCodeExpiredBehaviorSubject$.next(true);
      this.notificationService.error({
        message: TranslateService.localize('error.search.external.code'),
      });
    }
  }

  public openModalQrCode() {
    monitorLogger.screen.trackScreen('videoconference', 'clicar_icone_buscar_anexo');
    this.ModalQrCodeRef = this.modalOverlayService.openOverlay<ModalQrCodeComponent>(ModalQrCodeComponent);

    const { minutes, seconds } = this.timerBehaviorSubject$.value;

    if ((!this.hasQrCodeBeenGenerated || (minutes === 0 && seconds === 0)) && !this.isQrCodeActive) {
      this.generateQrCode(this.hasQrCodeBeenGenerated);
      this.hasQrCodeBeenGenerated = true;
    }

    this.ModalQrCodeRef.instance.qrCode$ = this.qrCodeObservable;
    this.ModalQrCodeRef.instance.timer$ = this.timerObservable;
    this.ModalQrCodeRef.instance.isQrCodeExpired$ = this.isQrCodeExpiredObservable;

    this.ModalQrCodeRef.instance.regenerate$.subscribe(() => {
      this.isQrCodeActive = true;
      this.generateQrCode(true);
    });

    this.ModalQrCodeRef.instance.close$.subscribe(() => {
      this.ModalQrCodeRef.close();
    });
  }

  public toggleChat(): void {
    this.showChat = !this.showChat;
  }

  public async join(): Promise<void> {
    this.videoCallManagerService.confirmCallStart();

    try {
      await this.videoconference.join();
    } catch (error) {
      console.error('failed to join videoconference', error);
      this.notificationService.error({ message: TranslateService.localize('errors.unexpected') });
    }
  }

  public async confirmJoin(): Promise<void> {
    monitorLogger.screen.trackScreen('videoconference', 'clicar_botao_entrar_preview');

    if (this.canSkipPreview && this.skipPreviewNextTime) {
      this.mediaDevicePreferencesService.setSkipPreviewBeforeCall(true);
    }

    this.isPreviewMode = false;
    this.modalWindowRef.updateConfig({ hideCloseButton: true });
    await this.join();
  }

  public async cancelPreview(): Promise<void> {
    monitorLogger.screen.trackScreen('videoconference', 'fechar_preview_sem_confirmar');
    await this.videoconference.stopLocalPreview();
    await this.videoCallManagerService.endCurrentCall();
  }

  public async toggleVideo(): Promise<void> {
    this.videoconference
      .localVideoObservable()
      .subscribe((isVideoEnabled) => {
        if (isVideoEnabled) {
          monitorLogger.screen.trackScreen('videoconference', 'clicar_botao_desligar_camera');
        } else {
          monitorLogger.screen.trackScreen('videoconference', 'clicar_botao_ligar_camera');
        }
      })
      .unsubscribe();
    await this.videoconference.toggleVideo();
  }

  public async toggleAudio(): Promise<void> {
    this.videoconference
      .localAudioObservable()
      .subscribe((isAudioEnabled) => {
        if (isAudioEnabled) {
          monitorLogger.screen.trackScreen('videoconference', 'clique_botao_desligar_audio');
        } else {
          monitorLogger.screen.trackScreen('videoconference', 'clique_botao_ligar_audio');
        }
      })
      .unsubscribe();
    await this.videoconference.toggleSound();
  }

  public async left(): Promise<void> {
    monitorLogger.screen.trackScreen('videoconference', 'clicar_botao_desligar_chamada');
    this.modalOverlayService.confirm(TranslateService.localize('questions.are-you-sure?')).subscribe(async () => {
      await this.videoconference.left();
      await this.videoCallManagerService.endCurrentCall();
    });
  }

  public async exit() {
    await this.videoconference.left();
  }

  private observerEvents() {
    this.modalWindowRef.isFullscreenMode$.subscribe((isFullScreen: boolean) => {
      if (!this.disableRenderChat) {
        this.showChat = isFullScreen;
        this.disableChatBehavior$.next(isFullScreen);
      }
    });

    this.modalWindowRef.closeWindow$.subscribe(() => {
      if (this.isPreviewMode) {
        this.cancelPreview();
      }
    });
  }

  private setupModal() {
    this.modalWindowRef.updateConfig({
      disableChangeWindowMode: this.mainProfileTypeDTO.profileType !== EProfileTypeDTO.Patient,
      hideCloseButton: !this.isPreviewMode,
    });
  }

  private renderChat() {
    if (this.disableRenderChat) {
      this.disableChatBehavior$.next(false);
      this.showChat = false;
    }
  }

  ngOnDestroy(): void {
    this.uploadSessionFilesSubscription?.unsubscribe();
    this.uploadSessionService.cleanupSession().subscribe();
    this.clearCountdown();
  }

  private subscribeToActiveQrSessionFiles(): void {
    this.uploadSessionFilesSubscription?.unsubscribe();
    this.uploadSessionFilesSubscription = this.uploadSessionService.files$
      .pipe(debounceTime(QR_SESSION_FILES_DEBOUNCE_MS))
      .subscribe((files) => {
        if (files.length === 0 || this.isSubmittingActiveQrSessionFiles) {
          return;
        }

        this.isSubmittingActiveQrSessionFiles = true;
        this.uploadSessionService
          .submitFilesForActiveRequisition(this.requisition.chatId, this.requisition.id)
          .subscribe({
            next: () => {
              this.isSubmittingActiveQrSessionFiles = false;
              this.notificationService.success({
                message: TranslateService.localize('upload.success'),
              });
            },
            error: () => {
              this.isSubmittingActiveQrSessionFiles = false;
              this.notificationService.error({
                message: TranslateService.localize('error.default.upload-files'),
              });
            },
          });
      });
  }
}
